import React, { useState } from 'react';
import { PilotData } from '../../types';
import PilotCard from './PilotCard';

interface PilotSidebarProps {
  pilots: Map<string, PilotData>;
  selectedPilot: string | null;
  followingPilot: string | null;
  onSelectPilot: (pilotId: string | null) => void;
  onCenterPilot: (pilot: PilotData) => void;
  onFollowPilot: (pilotId: string | null) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const PilotSidebar: React.FC<PilotSidebarProps> = ({
  pilots,
  selectedPilot,
  followingPilot,
  onSelectPilot,
  onCenterPilot,
  onFollowPilot,
  open,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const sidebarOpen = open !== undefined ? open : internalOpen;
  const setSidebarOpen = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPilots = Array.from(pilots.values()).filter((pilot) =>
    pilot.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {/* Floating toggle button */}
      <button
        className={`sidebar-toggle-floating ${!sidebarOpen ? 'floating-expanded' : ''}`}
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        style={{ left: sidebarOpen ? '320px' : '20px' }}
      >
        {sidebarOpen ? '«' : '»'}
      </button>

      {/* Sidebar */}
      {sidebarOpen && (
        <div className="pilots-sidebar open">
          <div className="sidebar-content">
            <div className="sidebar-header">
              <div className="sidebar-title">
                <div className="sidebar-title-left">
                  <div className="sidebar-icon">🪂</div>
                  <div className="sidebar-title-text">
                    <h2>Live Pilots</h2>
                    <span className="sidebar-subtitle">Real-time tracking</span>
                  </div>
                </div>
                <span className="pilot-count">{pilots.size}</span>
              </div>
            </div>

            <div className="search-container">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="pilot-search-input"
                placeholder="Search pilots..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="search-clear-btn"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>

            <div className="pilots-list-container">
              {pilots.size === 0 ? (
                <div className="no-pilots">No pilots currently tracked</div>
              ) : filteredPilots.length === 0 ? (
                <div className="no-pilots">No pilots found matching "{searchQuery}"</div>
              ) : (
                filteredPilots.map((pilot) => (
                  <PilotCard
                    key={pilot.id}
                    pilot={pilot}
                    isSelected={selectedPilot === pilot.id}
                    isFollowing={followingPilot === pilot.id}
                    onSelect={onSelectPilot}
                    onCenter={onCenterPilot}
                    onFollow={onFollowPilot}
                  />
                ))
              )}
            </div>
          </div>

          {/* Logo at bottom of sidebar */}
          <div className="sidebar-logo" onClick={(e) => e.stopPropagation()}>
            <a href="https://www.hikeandfly.app" target="_blank" rel="noopener noreferrer">
              <img
                src={`${process.env.PUBLIC_URL}/hfss_logo.png`}
                alt="HFSS"
                className="logo-large"
              />
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default PilotSidebar;